import {Component, Input, OnInit} from '@angular/core';
import {ColDef, ICellRendererParams, ValueGetterParams} from 'ag-grid-community';
import {takeUntil} from 'rxjs';
import {BaseComponent} from '../../base-component';
import {Conflict} from '../../common/data/conflict';
import {AssertionUtils} from '../../common/utils/assertion-utils';
import {StringUtils} from '../../common/utils/string-utils';
import {ColDefBuilderFactoryService} from '../../grids/col-def-builder-factory.service';
import {GridOptionsBuilder} from '../../grids/grid-options-builder';
import {LinkIconRendererComponent} from '../../grids/link-icon-renderer/link-icon-renderer.component';
import {OverlayComponentParams} from '../../grids/overlay/overlay-component-params';
import {TranslateService} from '../../translation/translate.service';
import {CrudObjectSelectionDialogInput} from '../crud-overview-data/crud-object-selection-dialog/crud-object-selection-dialog-input.interface';
import {CrudObjectSelectionDialogComponent} from '../crud-overview-data/crud-object-selection-dialog/crud-object-selection-dialog.component';
import {CrudOverviewDataBuilderFactoryService} from '../crud-overview-data/crud-overview-data-builder-factory-service/crud-overview-data-builder-factory.service';
import {CrudOverviewDataConfig} from '../crud-overview-data/interfaces/crud-overview-data-config.interface';
import {ContentSwitcherNavigationInput} from '../navigation-helper-prototype/content-switcher-navigation-input.interface';
import {NavigationHelperPrototypeService} from '../navigation-helper-prototype/navigation-helper-prototype.service';
import {LockIconConfig} from './lock-icon-config.interface';

@Component({
  selector: 'vdw-lock-icon',
  templateUrl: './lock-icon.component.html',
  styleUrl: './lock-icon.component.scss'
})
export class LockIconComponent extends BaseComponent implements OnInit {
  @Input() public iconClass = 'icon-12';
  @Input() public config: LockIconConfig;

  public canClick = false;
  private usedByConfig: CrudOverviewDataConfig<Conflict>;

  public constructor(
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<Conflict>,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly translate: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.canClick = !AssertionUtils.isNullOrUndefined(this.config);
    if (this.canClick) {
      this.usedByConfig = this.builderFactoryService
        .getBuilder()
        .withEntityName(this.translate.instant('ANGULAR_COMPONENT_LIBRARY.LOCK_ICON.USED_BY', {object: this.config.name}))
        .withRowData(this.config.conflictsObservable)
        .withGridOptions(this.getColumnDefs(), 'usedBy', null, (builder: GridOptionsBuilder) =>
          builder
            .withoutRowSelection()
            .withLoadingOverlay({
              scale: 0.7
            } as OverlayComponentParams)
            .build()
        )
        .build();
    }
  }

  public openUsedByDialog(event: Event): void {
    event.stopPropagation();
    this.navigationHelper
      .navigateWithContentSwitcher<Conflict[]>({
        componentInfo: {
          component: CrudObjectSelectionDialogComponent,
          componentInputs: {
            config: this.usedByConfig,
            canShowGridActions: false,
            canShowFooterActions: false
          } as CrudObjectSelectionDialogInput
        }
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((data: any) => this.config.rowActionsComplete?.next(data));
  }

  private getColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('name', true)
        .withHeaderName('GENERAL.NAME')
        .withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
          const editRoute = this.config.conflictType.getRouteForConflictType(params.data.type);
          return {
            params,
            canClick: !AssertionUtils.isNullOrUndefined(editRoute),
            contentSwitcherEntry: {
              componentInfo: {component: editRoute?.component},
              data: editRoute?.data
            } as ContentSwitcherNavigationInput
          };
        })
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('type')
        .withHeaderName('GENERAL.TYPE')
        .withValueGetter((params: ValueGetterParams) => {
          const translationKey = this.config.conflictType.getTranslationKeyForConflictType(params.data.type);
          return AssertionUtils.isNullOrUndefined(translationKey) ? params.data.type : this.translate.instant(translationKey, {count: 1});
        }, true)
        .withComparator(StringUtils.stringComparator)
        .build()
    ];
  }
}
